import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

export const menuRowTones = [
  /** Default — neutral hover. */
  'neutral',
  /** Currently-selected row (e.g. the active trip in the trip selector). */
  'selected',
  /** Secondary accent — for action rows like "New trip". */
  'secondary',
  /** Destructive — for actions like "Delete trip". Renamed from
   *  `destructive` to align tone vocabulary across IconButton /
   *  FeaturePill / MenuRow (all use `error` for the danger ramp). */
  'error',
] as const;
export type MenuRowTone = (typeof menuRowTones)[number];

const toneClasses: Record<MenuRowTone, string> = {
  neutral: 'text-content-text hover:bg-content-focal',
  selected: 'bg-primary-subtle text-primary-focal',
  secondary: 'text-secondary-emphasized hover:bg-secondary-subtle',
  error: 'text-error-main hover:bg-error-badge',
};

type NativeButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'children'
>;

export interface MenuRowProps extends NativeButtonProps {
  /** Optional leading icon. */
  icon?: IconDefinition;
  iconSize?: number;
  /** Trailing element (count, chevron, badge, etc.). */
  trailing?: React.ReactNode;
  tone?: MenuRowTone;
  /** Compact vs. default padding. Compact suits dense action rows like
   * "New trip" / "Import trip". */
  compact?: boolean;
  className?: ClassName;
  children: React.ReactNode;
}

/**
 * Standard hover-on-focal row used inside dropdown menus. Single line of
 * content with optional leading icon and trailing slot. Consolidates the
 * trip-selector trip rows, the new-trip / import-trip actions, the mode
 * picker tiles in TripModeMenu, and any future dropdown rows.
 */
export const MenuRow = forwardRef<HTMLButtonElement, MenuRowProps>(
  (
    {
      icon,
      iconSize = 10,
      trailing,
      tone = 'neutral',
      compact = false,
      className,
      type,
      children,
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={twx(
        'w-full flex items-center gap-2 rounded-xl text-xs font-medium transition-[colors,transform] duration-150 text-left cursor-pointer motion-safe:active:scale-[0.98]',
        compact ? 'px-3 py-1.5' : 'px-3 py-2',
        toneClasses[tone],
        className,
      )}
      {...rest}
    >
      {icon && (
        <FontAwesomeIcon
          icon={icon}
          style={{ width: iconSize, height: iconSize }}
          className="shrink-0"
        />
      )}
      <span className="flex-1 min-w-0">{children}</span>
      {trailing && <span className="shrink-0">{trailing}</span>}
    </button>
  ),
);
MenuRow.displayName = 'MenuRow';
